import * as React from 'react';
import { parse } from 'query-string';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { TournamentTeamPlayersManager } from 'Src/views/GenericView/AdminView/Tournaments/TournamentTeamPlayersManager';
import {
  getSchoolUnionTournamentTeam,
  getSchoolUnionTournamentTeamPlayers,
  getSchoolUnionTournamentTeamPlayersCount,
  updateSchoolUnionTournamentTeamPlayersBatch,
  updateSchoolUnionTournamentTeamPlayerQuantumNumber,
  assignSchoolUnionTournamentTeamPlayerNumbersToTeam,
  assignSchoolUnionTournamentTeamPlayerNumbersToTournament
} from 'Src/helpers/service/adminSU/tournamentTeamPlayersTable';
import {
  getSchoolUnionSchoolStudents,
  getSchoolUnionSchoolStudentsCount,
  getAllSchoolUnionSchoolStudents,
  getAllSchoolUnionSchoolForms,
  getAllSchoolUnionSchoolHouses,
  addSchoolUnionSchoolStudent,
  updateSchoolUnionSchoolStudent
} from 'Src/helpers/service/adminSU/schoolStudentsTable';
import { getSchoolUnionSchool } from 'Src/helpers/service/adminSU/schoolsTable';
import { getTournament } from 'Src/helpers/service/adminSU/tournamentsTable';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export default function AdminSUTeamMembers(props: Props) {
  const search = parse(props.history.location.search);
  const tournamentId = (search.tournament as string) || (search.tournamentId as string);
  const teamId = (search.teamId as string) || '';

  const ownerSchoolId = props.user.activeSchoolId || (search.ownerSchoolId as string) || '';

  const contextSchoolId = (search.contextSchoolId as string) || '';
  const queryContextSchoolKind = (search.contextSchoolKind as string) || '';
  const shouldResolveContextSchoolKind =
    contextSchoolId && ownerSchoolId && contextSchoolId !== ownerSchoolId && !queryContextSchoolKind;
  const [resolvedContextSchoolKind, setResolvedContextSchoolKind] = React.useState(
    shouldResolveContextSchoolKind ? '' : queryContextSchoolKind || 'School'
  );

  React.useEffect(() => {
    let isCancelled = false;

    if (!shouldResolveContextSchoolKind) {
      setResolvedContextSchoolKind(queryContextSchoolKind || 'School');
      return;
    }

    getSchoolUnionSchool(props.user, contextSchoolId)
      .then(contextSchool => {
        if (isCancelled) {
          return;
        }

        const contextSchoolKind =
          contextSchool?.kind ||
          (Array.isArray(contextSchool?.schoolIds) ? 'SchoolUnion' : queryContextSchoolKind || 'School');

        setResolvedContextSchoolKind(contextSchoolKind);
      })
      .catch(error => {
        console.error('[AdminSUTeamMembers] Error resolving context school kind', error);

        if (!isCancelled) {
          setResolvedContextSchoolKind(queryContextSchoolKind || 'School');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [props.user, contextSchoolId, ownerSchoolId, queryContextSchoolKind, shouldResolveContextSchoolKind]);

  const contextSchoolKind = resolvedContextSchoolKind || 'School';
  const userForContext: AppUser = React.useMemo(
    () => ({
      ...props.user,
      activeSchoolId: contextSchoolId,
      activeSchool: { ...(props.user.activeSchool || {}), id: contextSchoolId, kind: contextSchoolKind } as any
    }),
    [props.user, contextSchoolId, contextSchoolKind]
  );

  if (!tournamentId || !teamId || !ownerSchoolId || !contextSchoolId) {
    console.error('[AdminSUTeamMembers] Missing required ids', {
      tournamentId,
      teamId,
      ownerSchoolId,
      contextSchoolId
    });
    return (
      <div className="mt-3 text-danger">
        Missing parameters. Please navigate here from the Teams screen.
        <br />
        Needed: tournamentId, teamId, ownerSchoolId, contextSchoolId.
      </div>
    );
  }

  const api = React.useMemo(
    () => ({
      getTournament: (user, tId) => getTournament(user, tId, true),
      getTeam: (user, tId, tmId) => getSchoolUnionTournamentTeam(user, ownerSchoolId, tId, tmId),
      getForms: user =>
        getAllSchoolUnionSchoolForms(user, contextSchoolId).catch(error => {
          if (error?.response?.status === 404) {
            return [];
          }
          throw error;
        }),
      getHouses: user => getAllSchoolUnionSchoolHouses(user, contextSchoolId),
      getAllTeamPlayers: (user, tId, tmId, where) =>
        getSchoolUnionTournamentTeamPlayers(
          user,
          ownerSchoolId,
          tId,
          tmId,
          {
            where: where || {},
            limit: DEFAULT_LIMIT,
            skip: DEFAULT_SKIP
          },
          contextSchoolId
        ),
      getTeamPlayers: (user, tId, tmId, filter) =>
        getSchoolUnionTournamentTeamPlayers(user, ownerSchoolId, tId, tmId, filter, contextSchoolId),
      getTeamPlayersCount: (user, tId, tmId, where) =>
        getSchoolUnionTournamentTeamPlayersCount(user, ownerSchoolId, tId, tmId, where, contextSchoolId),
      updateTeamPlayersBatch: (user, tId, tmId, data) =>
        updateSchoolUnionTournamentTeamPlayersBatch(user, ownerSchoolId, tId, tmId, data),
      updatePlayerQuantumNumber: (user, tId, tmId, playerId, data) =>
        updateSchoolUnionTournamentTeamPlayerQuantumNumber(user, ownerSchoolId, tId, tmId, playerId, data),
      assignPlayerNumbersToTeam: (user, tId, tmId, data) =>
        assignSchoolUnionTournamentTeamPlayerNumbersToTeam(user, ownerSchoolId, tId, tmId, data),
      assignPlayerNumbersToTournament: (user, tId, data) =>
        assignSchoolUnionTournamentTeamPlayerNumbersToTournament(user, ownerSchoolId, tId, data),
      getStudents: (user, filter) => getSchoolUnionSchoolStudents(user, contextSchoolId, filter),
      getStudentsCount: (user, where) => getSchoolUnionSchoolStudentsCount(user, contextSchoolId, where),
      getAllStudents: (user, where) => getAllSchoolUnionSchoolStudents(user, contextSchoolId, where),
      addStudent: (user, data) => addSchoolUnionSchoolStudent(user, contextSchoolId, data),
      updateStudent: (user, studentId, data) => updateSchoolUnionSchoolStudent(user, contextSchoolId, studentId, data)
    }),
    [ownerSchoolId, contextSchoolId]
  );

  if (!resolvedContextSchoolKind) {
    return null;
  }

  return (
    <TournamentTeamPlayersManager
      {...props}
      user={props.user}
      api={api}
      isPlayerNumbersEnabled={true}
      isSchoolColumnEnabled={true}
      isAgeGroupColumnEnabled={true}
      isUnionTeamStudentFormEnabled={true}
      isUnionSide={true}
      unionTeamStudentFormUser={props.user}
      studentFormUser={userForContext}
    />
  );
}
